import React, {useEffect} from "react";
import {useAppDispatch, useAppSelector} from "../../../reducers/hooks";
import {
    getAllElement,
    MunicipalityElement,
    saveElement,
    updateElement
} from "../../../reducers/Municipality/MunicipalityReducer";
import {Form, FormikProvider, useFormik} from "formik";
import * as Yup from "yup";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormHelperText,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import {FormattedMessage, useIntl} from "react-intl";
import {ColumnFiltersState} from "@tanstack/react-table";

interface MuniFormProps {
    closeModal: () => void;
    element: MunicipalityElement | null;
    onSuccess?: () => void;
    pageableData?: {
        page: number;
        rowsPerPage: number;
        filters: ColumnFiltersState | null;
    };
}

export const MunicipalityElementForm: React.FC<MuniFormProps> = ({
                                                                     closeModal,
                                                                     element,
                                                                     onSuccess,
                                                                     pageableData
                                                                 }) => {
    const dispatch = useAppDispatch();

    // Seleziona i dati dal Redux store
    const province = useAppSelector((state) => state.municipalityElement.elements);
    const intl = useIntl()

    // Effettua il dispatch dell'azione per ottenere i dati dei municipi
    useEffect(() => {
        dispatch(getAllElement());
    }, [dispatch]);

    const formik = useFormik({
        initialValues: element ? element : {
            _id: '',
            name: '',
            province: '',
            postalCode: '',
        },

        validationSchema: Yup.object().shape({
            name: Yup.string().min(2, "Length not valid, min 2 characters").required(intl.formatMessage({id: "The name is a required field"})),
            province: Yup.string().required(intl.formatMessage({id: "The province is required"})),
            postalCode: Yup.string().matches(/^\d{1,5}$/, intl.formatMessage({id: "The postal code format is not correct , 5 numers required"})).required(intl.formatMessage({id: "The postal code is required"})),
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                if (element && element._id) {
                    dispatch(updateElement({
                        data: values,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }))
                } else {
                    dispatch(saveElement({
                        data: values,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }))
                }
                closeModal();
            } catch (error) {
                console.error('Error:', error);
            }
        }
    });

    //const selectedprovince = province.find(province=> province.name === formik.values.province) || null

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                <DialogTitle>{intl.formatMessage({id: "new municipality"})}</DialogTitle>
                <Divider/>
                <DialogContent sx={{p: 2.5}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="name">
                                    <FormattedMessage id="Name" defaultMessage="name"/>
                                </InputLabel>
                                <TextField
                                    fullWidth
                                    id="name"
                                    placeholder={intl.formatMessage({id: "Name"})}
                                    {...formik.getFieldProps('name')}
                                    error={Boolean(formik.touched.name && formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="province">
                                    <FormattedMessage id="province" defaultMessage="province"/>
                                </InputLabel>
                                <Select
                                    id="province"
                                    {...formik.getFieldProps('province')}
                                    value={formik.values.province}
                                    onChange={(e) => formik.setFieldValue('province', e.target.value)}
                                >
                                    <MenuItem value="MT">
                                        <FormattedMessage id="MT" defaultMessage="MT"/>
                                    </MenuItem>
                                    <MenuItem value="PZ">
                                        <FormattedMessage id="PZ" defaultMessage="PZ"/>
                                    </MenuItem>

                                </Select>
                                {formik.touched.province && formik.errors.province && (
                                    <FormHelperText error>{formik.errors.province}</FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1}>
                                <InputLabel htmlFor="postalCode">
                                    <FormattedMessage id="postalCode" defaultMessage="postalCode"/>
                                </InputLabel>
                                <TextField
                                    fullWidth
                                    id="postalCode"
                                    placeholder={intl.formatMessage({id: "postalCode"})}
                                    {...formik.getFieldProps('postalCode')}
                                    error={Boolean(formik.touched.postalCode && formik.errors.postalCode)}
                                    helperText={formik.touched.postalCode && formik.errors.postalCode}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <Divider/>
                <DialogActions sx={{p: 2.5}}>
                    <Grid container justifyContent="flex-end" alignItems="center">
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button color="error" onClick={closeModal}>
                                    <FormattedMessage id="Cancel" defaultMessage="Cancel"/>
                                </Button>
                                <Button type="submit" variant="contained">
                                    <FormattedMessage id="Save" defaultMessage="Save"/>
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Form>
        </FormikProvider>
    );
}
